export const content = {
  de: `
# KI-gesteuerte CRM-Suche: Wie Close.com die Vertriebsarbeit revolutioniert

Erinnerst Du Dich noch an die Zeiten, als CRM-Suchen so aussahen, dass man dachte, man müsste erst eine Programmierausbildung abschließen, um vernünftige Suchen zu erstellen?

Die gute Nachricht: Diese Zeiten sind vorbei.

Close.com hat gerade eine bahnbrechende Funktion eingeführt: KI-gestützte Suche. In diesem Artikel zeige ich Dir, wie diese Innovation die Art und Weise revolutioniert, wie Du mit Deinem CRM arbeitest – und warum Du keine technischen Kenntnisse mehr brauchst.

## Die Evolution der CRM-Suche: Von Queries zu KI

### Phase 1: Die dunklen Zeiten der Text-Queries

Früher sah eine CRM-Suche so aus:

\`\`\`
Kampagne = "Outbound" AND 
ausgehende_anrufe NOT IN last_3_days AND
task_status = "open"
\`\`\`

**Das Problem:**

- Komplizierte Syntax
- Fehleranfällig
- Nur für technisch versierte Nutzer
- Zeitaufwendig

**Das Ergebnis:** Die meisten Vertriebler nutzten nur Basis-Funktionen, weil komplexe Suchen zu schwierig waren.

### Phase 2: Aufgabenblöcke und Filter

Der nächste Entwicklungsschritt waren visuelle Aufgabenblöcke:

- Filter über Benutzeroberfläche auswählen
- Drag & Drop von Bedingungen
- Keine Textsuchen mehr nötig

Besser, aber immer noch:

- Viele Klicks erforderlich
- Komplexe Logik schwer abzubilden
- Lernkurve vorhanden

### Phase 3: KI-gestützte Suche (JETZT)

Die Revolution: Du schreibst einfach in natürlicher Sprache, was Du suchst.

**Beispiel:**

"Zeig mir alle Leads, die noch keinen Anruf haben"

**Das System:**

- Versteht Deine Anfrage
- Generiert automatisch die Filter
- Zeigt Dir die Ergebnisse

Keine Programmierung. Keine komplexen Filter. Einfach fragen.

## Wie die KI-Suche in Close.com funktioniert

### Schritt 1: AI Search aktivieren

In Close.com findest Du jetzt drei Such-Optionen:

- **Lead Search** – Klassische Lead-Suche
- **Contact Search** – Kontakt-Suche
- **AI Search** – Die neue KI-gestützte Suche ✨

### Schritt 2: In natürlicher Sprache fragen

Du kannst auf Deutsch oder Englisch suchen.

**Beispiel 1: Leads ohne Anrufe finden**

**Eingabe:** "Zeig mir alle Leads, die noch keinen Anruf haben"

**Was passiert:**

- KI analysiert Deine Anfrage
- Generiert automatisch Filter: "Total number of calls = 0"
- Zeigt alle Leads ohne Anrufe

**Beispiel 2: Kampagnen-spezifische Suche**

**Eingabe:** "Gib mir alle Leads aus der Kampagne Testkampagne, die weniger als 10 Anrufe haben und keine offene Task"

**Was passiert:**

- Filter 1: Kampagne = "Testkampagne"
- Filter 2: Total number of calls < 10
- Filter 3: Task status = "completed" oder "none"

**Das Beste:** Du siehst die generierten Filter und kannst sie manuell anpassen.

### Schritt 3: Ergebnisse nutzen

Die Suchergebnisse kannst Du:

- Als Anrufliste speichern
- Für Kampagnen nutzen
- Für Reportings verwenden
- Exportieren

## 7 praktische Anwendungsfälle für KI-Suche

### Anwendungsfall #1: Neue Anruflisten erstellen

**Problem:** Du startest eine neue Kampagne und brauchst eine spezifische Anrufliste.

**Lösung mit KI-Suche:**

**Eingabe:** "Zeig mir alle Leads aus der Kampagne 'Q1 Outbound', die in den letzten 7 Tagen nicht angerufen wurden und eine Priorität über 50 haben"

**Ergebnis:** Perfekt priorisierte Anrufliste in Sekunden.

**Zeitersparnis:** Von 10 Minuten manueller Filterarbeit auf 30 Sekunden.

### Anwendungsfall #2: KPI-Auswertungen

**Problem:** Du willst wissen, wie viele Setting-Termine gestern gebucht wurden.

**Lösung mit KI-Suche:**

**Eingabe:** "Gib mir alle Leads, bei denen gestern ein Setting gebucht wurde"

**Ergebnis:** Sofortige Übersicht aller gestrigen Settings.

**Weitere KPI-Beispiele:**

- "Zeig mir alle Leads, wo gestern ein Closing gebucht wurde"
- "Gib mir alle Leads, die diese Woche zu einem Termin erschienen sind"
- "Zeig mir alle No-Shows der letzten 7 Tage"

### Anwendungsfall #3: Notizen durchsuchen

**Problem:** Früher gab es keine Möglichkeit, in Notizen zu suchen.

**Lösung mit KI-Suche:**

**Eingabe:** "Gib mir alle Leads, wo in der Notiz 'GK' drin steht"

(GK = Gatekeeper)

**Ergebnis:** Alle Leads, bei denen Du mit einem Gatekeeper gesprochen hast.

**Weitere Notiz-Suchen:**

- "Zeig mir alle Leads mit 'Budget vorhanden' in der Notiz"
- "Gib mir alle Leads mit 'Entscheider erreicht' in der Notiz"
- "Zeig mir alle Leads mit 'Follow-up Q2' in der Notiz"

### Anwendungsfall #4: Priorisierung

**Problem:** Du willst Dich auf die wichtigsten Leads konzentrieren.

**Lösung mit KI-Suche:**

**Eingabe:** "Zeig mir alle Leads mit höchster Priorität"

oder

"Zeig mir alle Leads mit Lead Score über 80"

**Ergebnis:** Fokussierte Liste der wertvollsten Leads.

### Anwendungsfall #5: Follow-up-Management

**Problem:** Du willst alle Leads finden, die ein Follow-up brauchen.

**Lösung mit KI-Suche:**

**Eingabe:** "Zeig mir alle Leads, die in den letzten 14 Tagen nicht kontaktiert wurden und eine offene Task haben"

**Ergebnis:** Alle Leads, die dringend ein Follow-up brauchen.

### Anwendungsfall #6: Kampagnen-Performance analysieren

**Problem:** Du willst wissen, welche Kampagne am besten performt.

**Lösung mit KI-Suche:**

**Eingabe:** "Zeig mir alle Leads aus Kampagne A, die zu einem Closing-Termin konvertiert haben"

Dann: "Zeig mir alle Leads aus Kampagne B, die zu einem Closing-Termin konvertiert haben"

**Ergebnis:** Direkter Vergleich der Kampagnen-Performance.

### Anwendungsfall #7: Vertriebsmitarbeiter-Performance

**Problem:** Du willst sehen, wie viele Termine ein bestimmter Vertriebler gebucht hat.

**Lösung mit KI-Suche:**

**Eingabe:** "Zeig mir alle Leads, die von [Name] bearbeitet wurden und diese Woche einen Termin gebucht haben"

**Ergebnis:** Performance-Übersicht pro Mitarbeiter.

## Die Vorteile der KI-Suche auf einen Blick

### Vorteil #1: Keine technischen Kenntnisse erforderlich

**Früher:**

- Komplexe Query-Syntax lernen
- Filter-Logik verstehen
- Stunden mit Trial & Error

**Jetzt:**

- Einfach fragen, was Du suchst
- In Deiner Sprache
- Sofortige Ergebnisse

### Vorteil #2: Massive Zeitersparnis

**Vergleich:**

**Manuelle Filter-Erstellung:**

- 5-10 Minuten pro komplexer Suche
- Fehleranfällig
- Muss bei jeder Änderung neu gemacht werden

**KI-Suche:**

- 30 Sekunden pro Suche
- Präzise
- Einfach anpassbar

**Zeitersparnis:** 90%+

### Vorteil #3: Mehr Möglichkeiten nutzen

**Das Problem mit komplexen Systemen:**

Die meisten Nutzer verwenden nur 20% der Funktionen, weil der Rest zu kompliziert ist.

**Mit KI-Suche:**

Du kannst jetzt auch komplexe Suchen erstellen, ohne die technischen Details zu kennen.

**Das Ergebnis:** Du nutzt das volle Potenzial Deines CRMs.

### Vorteil #4: Schnellere Entscheidungen

**Szenario:** Dein Chef fragt: "Wie viele Termine haben wir diese Woche gebucht?"

**Früher:**

- 5 Minuten Filter erstellen
- Exportieren
- Zählen

**Jetzt:**

- 30 Sekunden KI-Suche
- Sofortige Antwort

### Vorteil #5: Bessere Vertriebsstrategien

**Warum?**

Weil Du jetzt schnell verschiedene Szenarien testen kannst:

- "Was wäre, wenn wir nur Leads mit 5+ Anrufen kontaktieren?"
- "Wie viele Leads haben wir in Kampagne X mit hoher Priorität?"
- "Welche Leads haben wir in den letzten 30 Tagen nicht kontaktiert?"

**Schnellere Tests = Bessere Strategien**

## Praktische Tipps für die KI-Suche

### Tipp #1: Sei spezifisch

**Weniger gut:** "Zeig mir Leads"

**Besser:** "Zeig mir alle Leads aus der Kampagne Q1, die in den letzten 7 Tagen nicht angerufen wurden"

### Tipp #2: Nutze klare Zeitangaben

**Funktioniert gut:**

- "gestern"
- "diese Woche"
- "in den letzten 7 Tagen"
- "im letzten Monat"

### Tipp #3: Kombiniere mehrere Kriterien

**Beispiel:** "Zeig mir alle Leads mit Priorität über 70, die weniger als 5 Anrufe haben und keine offene Task"

### Tipp #4: Überprüfe die generierten Filter

Die KI zeigt Dir die erstellten Filter. Überprüfe sie und passe sie bei Bedarf an.

### Tipp #5: Speichere häufige Suchen

Wenn Du eine Suche regelmäßig brauchst, speichere sie als Smart View.

## Die Zukunft der CRM-Arbeit

### Was diese Innovation bedeutet

Die KI-Suche in Close.com ist mehr als nur ein Feature – es ist ein Paradigmenwechsel:

**Von:** "Wie bediene ich das System?"  
**Zu:** "Was will ich erreichen?"

**Das bedeutet:**

**Demokratisierung von CRM-Power**

- Jeder kann komplexe Suchen erstellen
- Keine technischen Barrieren mehr
- Schnellere Einarbeitung neuer Mitarbeiter

**Fokus auf Strategie statt Technik**

- Mehr Zeit für Vertrieb
- Weniger Zeit für System-Administration
- Bessere Ergebnisse

**Schnellere Iteration**

- Teste verschiedene Ansätze in Minuten
- Optimiere kontinuierlich
- Lerne schneller, was funktioniert

### Was als Nächstes kommt

Die KI-Suche ist erst der Anfang. In Zukunft werden wir sehen:

- **Predictive Search:** "Zeig mir Leads, die wahrscheinlich diese Woche kaufen"
- **Automatische Insights:** "Diese Leads solltest Du heute anrufen"
- **Conversational CRM:** Komplette CRM-Steuerung per Sprache

## Häufige Fragen zur KI-Suche

### "Funktioniert es wirklich auf Deutsch?"

Ja! Die KI versteht sowohl Deutsch als auch Englisch.

### "Was wenn die KI meine Anfrage nicht versteht?"

Formuliere sie etwas anders oder sei spezifischer. Die KI lernt kontinuierlich dazu.

### "Kann ich die generierten Filter anpassen?"

Ja! Du siehst alle Filter und kannst sie manuell ändern.

### "Kostet die KI-Suche extra?"

Das hängt von Deinem Close.com-Plan ab. Kontaktiere Close für Details.

### "Ersetzt die KI-Suche die normalen Filter?"

Nein. Du kannst weiterhin manuelle Filter nutzen. Die KI-Suche ist eine zusätzliche Option.

## Praxisbeispiel: Ein Tag mit KI-Suche

### 9:00 Uhr – Morning Briefing

**Chef:** "Wie viele Termine haben wir gestern gebucht?"

**Du:** KI-Suche: "Zeig mir alle Leads, wo gestern ein Termin gebucht wurde"

**Antwort in 30 Sekunden:** "23 Termine"

### 10:00 Uhr – Neue Kampagne starten

Du brauchst eine Anrufliste für eine neue Outbound-Kampagne.

**KI-Suche:** "Zeig mir alle Leads aus Kampagne Q1 Outbound, die weniger als 3 Anrufe haben und in den letzten 14 Tagen nicht kontaktiert wurden"

**Anrufliste erstellt in 1 Minute.**

### 14:00 Uhr – Follow-up-Check

Du willst sicherstellen, dass keine Leads vergessen werden.

**KI-Suche:** "Zeig mir alle Leads mit offener Task, die in den letzten 7 Tagen nicht kontaktiert wurden"

**5 Leads gefunden, die Follow-up brauchen.**

### 16:00 Uhr – Performance-Review

Du willst wissen, welche Kampagne am besten läuft.

**KI-Suche:** "Zeig mir alle Leads aus Kampagne A, die zu einem Closing konvertiert haben"

**KI-Suche:** "Zeig mir alle Leads aus Kampagne B, die zu einem Closing konvertiert haben"

**Direkter Vergleich in 2 Minuten.**

**Zeitersparnis an einem Tag:** Mindestens 1 Stunde.

## Zusammenfassung: Warum KI-Suche ein Game-Changer ist

### Die 5 wichtigsten Vorteile:

- ✅ **Keine technischen Kenntnisse nötig** – Jeder kann komplexe Suchen erstellen
- ✅ **90% Zeitersparnis** – Von 10 Minuten auf 30 Sekunden
- ✅ **Mehr Möglichkeiten nutzen** – Volle CRM-Power ohne Komplexität
- ✅ **Schnellere Entscheidungen** – Sofortige Antworten auf Fragen
- ✅ **Bessere Strategien** – Schnelles Testen verschiedener Ansätze

### Die wichtigsten Anwendungsfälle:

- ✅ Anruflisten erstellen
- ✅ KPI-Auswertungen
- ✅ Notizen durchsuchen
- ✅ Priorisierung
- ✅ Follow-up-Management
- ✅ Kampagnen-Analyse
- ✅ Performance-Tracking

## Bereit, die KI-Suche zu nutzen?

Die KI-gestützte Suche in Close.com ist mehr als nur ein Feature – es ist eine Revolution in der Art, wie Du mit Deinem CRM arbeitest.

Die Frage ist: Willst Du weiterhin Stunden mit manuellen Filtern verschwenden – oder willst Du die Power der KI nutzen?

**Buche jetzt Deine kostenlose Demo** und ich zeige Dir, wie Du die KI-Suche optimal für Deinen Vertrieb einsetzt.
  `,
  en: `
# AI-Powered CRM Search: How Close.com is Revolutionizing Sales Work

Do you remember the times when CRM searches looked so complicated that you thought you needed to complete a programming course to create proper searches?

The good news: Those times are over.

Close.com has just introduced a groundbreaking feature: AI-powered search. In this article, I'll show you how this innovation revolutionizes the way you work with your CRM – and why you no longer need technical knowledge.

## The Evolution of CRM Search: From Queries to AI

### Phase 1: The Dark Ages of Text Queries

Previously, a CRM search looked like this:

\`\`\`
Campaign = "Outbound" AND 
outgoing_calls NOT IN last_3_days AND
task_status = "open"
\`\`\`

**The problem:**

- Complicated syntax
- Error-prone
- Only for technically savvy users
- Time-consuming

**The result:** Most sales reps only used basic functions because complex searches were too difficult.

### Phase 2: Task Blocks and Filters

The next development step was visual task blocks:

- Select filters via user interface
- Drag & drop conditions
- No more text searches needed

Better, but still:

- Many clicks required
- Complex logic hard to represent
- Learning curve present

### Phase 3: AI-Powered Search (NOW)

The revolution: You simply write what you're looking for in natural language.

**Example:**

"Show me all leads that haven't been called yet"

**The system:**

- Understands your query
- Automatically generates filters
- Shows you the results

No programming. No complex filters. Just ask.

## How AI Search Works in Close.com

### Step 1: Activate AI Search

In Close.com you now find three search options:

- **Lead Search** – Classic lead search
- **Contact Search** – Contact search
- **AI Search** – The new AI-powered search ✨

### Step 2: Ask in Natural Language

You can search in German or English.

**Example 1: Find Leads Without Calls**

**Input:** "Show me all leads that haven't been called yet"

**What happens:**

- AI analyzes your query
- Automatically generates filter: "Total number of calls = 0"
- Shows all leads without calls

**Example 2: Campaign-Specific Search**

**Input:** "Give me all leads from the Test Campaign that have less than 10 calls and no open task"

**What happens:**

- Filter 1: Campaign = "Test Campaign"
- Filter 2: Total number of calls < 10
- Filter 3: Task status = "completed" or "none"

**The best part:** You see the generated filters and can manually adjust them.

### Step 3: Use Results

You can use the search results to:

- Save as call list
- Use for campaigns
- Use for reporting
- Export

## 7 Practical Use Cases for AI Search

### Use Case #1: Create New Call Lists

**Problem:** You're starting a new campaign and need a specific call list.

**Solution with AI Search:**

**Input:** "Show me all leads from the 'Q1 Outbound' campaign that weren't called in the last 7 days and have a priority above 50"

**Result:** Perfectly prioritized call list in seconds.

**Time savings:** From 10 minutes of manual filter work to 30 seconds.

### Use Case #2: KPI Evaluations

**Problem:** You want to know how many setting appointments were booked yesterday.

**Solution with AI Search:**

**Input:** "Give me all leads where a setting was booked yesterday"

**Result:** Immediate overview of all yesterday's settings.

**More KPI examples:**

- "Show me all leads where a closing was booked yesterday"
- "Give me all leads that attended an appointment this week"
- "Show me all no-shows from the last 7 days"

### Use Case #3: Search Notes

**Problem:** Previously there was no way to search in notes.

**Solution with AI Search:**

**Input:** "Give me all leads where 'GK' is in the note"

(GK = Gatekeeper)

**Result:** All leads where you spoke with a gatekeeper.

**More note searches:**

- "Show me all leads with 'Budget available' in the note"
- "Give me all leads with 'Decision maker reached' in the note"
- "Show me all leads with 'Follow-up Q2' in the note"

### Use Case #4: Prioritization

**Problem:** You want to focus on the most important leads.

**Solution with AI Search:**

**Input:** "Show me all leads with highest priority"

or

"Show me all leads with lead score above 80"

**Result:** Focused list of the most valuable leads.

### Use Case #5: Follow-up Management

**Problem:** You want to find all leads that need follow-up.

**Solution with AI Search:**

**Input:** "Show me all leads that weren't contacted in the last 14 days and have an open task"

**Result:** All leads that urgently need follow-up.

### Use Case #6: Analyze Campaign Performance

**Problem:** You want to know which campaign performs best.

**Solution with AI Search:**

**Input:** "Show me all leads from Campaign A that converted to a closing appointment"

Then: "Show me all leads from Campaign B that converted to a closing appointment"

**Result:** Direct comparison of campaign performance.

### Use Case #7: Sales Rep Performance

**Problem:** You want to see how many appointments a specific sales rep booked.

**Solution with AI Search:**

**Input:** "Show me all leads that were handled by [Name] and booked an appointment this week"

**Result:** Performance overview per employee.

## The Benefits of AI Search at a Glance

### Benefit #1: No Technical Knowledge Required

**Previously:**

- Learn complex query syntax
- Understand filter logic
- Hours of trial & error

**Now:**

- Simply ask what you're looking for
- In your language
- Immediate results

### Benefit #2: Massive Time Savings

**Comparison:**

**Manual Filter Creation:**

- 5-10 minutes per complex search
- Error-prone
- Must be redone with each change

**AI Search:**

- 30 seconds per search
- Precise
- Easily adjustable

**Time savings:** 90%+

### Benefit #3: Use More Capabilities

**The problem with complex systems:**

Most users only use 20% of the functions because the rest is too complicated.

**With AI Search:**

You can now create complex searches without knowing the technical details.

**The result:** You use the full potential of your CRM.

### Benefit #4: Faster Decisions

**Scenario:** Your boss asks: "How many appointments did we book this week?"

**Previously:**

- 5 minutes to create filter
- Export
- Count

**Now:**

- 30 seconds AI search
- Immediate answer

### Benefit #5: Better Sales Strategies

**Why?**

Because you can now quickly test different scenarios:

- "What if we only contact leads with 5+ calls?"
- "How many leads do we have in Campaign X with high priority?"
- "Which leads haven't we contacted in the last 30 days?"

**Faster tests = Better strategies**

## Practical Tips for AI Search

### Tip #1: Be Specific

**Less good:** "Show me leads"

**Better:** "Show me all leads from the Q1 campaign that weren't called in the last 7 days"

### Tip #2: Use Clear Time References

**Works well:**

- "yesterday"
- "this week"
- "in the last 7 days"
- "last month"

### Tip #3: Combine Multiple Criteria

**Example:** "Show me all leads with priority above 70, that have less than 5 calls and no open task"

### Tip #4: Review Generated Filters

The AI shows you the created filters. Review them and adjust if needed.

### Tip #5: Save Frequent Searches

If you need a search regularly, save it as a Smart View.

## The Future of CRM Work

### What This Innovation Means

AI search in Close.com is more than just a feature – it's a paradigm shift:

**From:** "How do I operate the system?"  
**To:** "What do I want to achieve?"

**This means:**

**Democratization of CRM Power**

- Everyone can create complex searches
- No more technical barriers
- Faster onboarding of new employees

**Focus on Strategy Instead of Technology**

- More time for sales
- Less time for system administration
- Better results

**Faster Iteration**

- Test different approaches in minutes
- Optimize continuously
- Learn faster what works

### What's Coming Next

AI search is just the beginning. In the future we'll see:

- **Predictive Search:** "Show me leads likely to buy this week"
- **Automatic Insights:** "You should call these leads today"
- **Conversational CRM:** Complete CRM control via voice

## Frequently Asked Questions About AI Search

### "Does it really work in German?"

Yes! The AI understands both German and English.

### "What if the AI doesn't understand my query?"

Rephrase it slightly or be more specific. The AI is continuously learning.

### "Can I adjust the generated filters?"

Yes! You see all filters and can manually change them.

### "Does AI search cost extra?"

That depends on your Close.com plan. Contact Close for details.

### "Does AI search replace normal filters?"

No. You can still use manual filters. AI search is an additional option.

## Practical Example: A Day with AI Search

### 9:00 AM – Morning Briefing

**Boss:** "How many appointments did we book yesterday?"

**You:** AI Search: "Show me all leads where an appointment was booked yesterday"

**Answer in 30 seconds:** "23 appointments"

### 10:00 AM – Start New Campaign

You need a call list for a new outbound campaign.

**AI Search:** "Show me all leads from Q1 Outbound campaign that have less than 3 calls and weren't contacted in the last 14 days"

**Call list created in 1 minute.**

### 2:00 PM – Follow-up Check

You want to ensure no leads are forgotten.

**AI Search:** "Show me all leads with open task that weren't contacted in the last 7 days"

**5 leads found that need follow-up.**

### 4:00 PM – Performance Review

You want to know which campaign performs best.

**AI Search:** "Show me all leads from Campaign A that converted to a closing"

**AI Search:** "Show me all leads from Campaign B that converted to a closing"

**Direct comparison in 2 minutes.**

**Time savings in one day:** At least 1 hour.

## Summary: Why AI Search is a Game-Changer

### The 5 Most Important Benefits:

- ✅ **No technical knowledge needed** – Everyone can create complex searches
- ✅ **90% time savings** – From 10 minutes to 30 seconds
- ✅ **Use more capabilities** – Full CRM power without complexity
- ✅ **Faster decisions** – Immediate answers to questions
- ✅ **Better strategies** – Quick testing of different approaches

### The Most Important Use Cases:

- ✅ Create call lists
- ✅ KPI evaluations
- ✅ Search notes
- ✅ Prioritization
- ✅ Follow-up management
- ✅ Campaign analysis
- ✅ Performance tracking

## Ready to Use AI Search?

AI-powered search in Close.com is more than just a feature – it's a revolution in how you work with your CRM.

The question is: Do you want to continue wasting hours with manual filters – or do you want to use the power of AI?

**Book your free demo now** and I'll show you how to optimally use AI search for your sales.
  `,
}
